/**
 * Notifications Store
 *
 * Centraliza TODAS las notificaciones del panel admin en un único store de UI.
 * Los datos vivos siguen viviendo en los stores de dominio (enrollments, chat,
 * agents); este store es una capa derivada que los snapshots en `AppNotification`
 * para alimentar el panel de la campana.
 *
 * El flag `read` es la única pieza que persiste (en localStorage) para que las
 * notificaciones no reaparezcan como "no leídas" cada vez que se reconcilian.
 * Las notificaciones completas NO persisten: si recargás la página, el provider
 * las reconstruye desde los stores vivos y preserva `read` para los ids que
 * coinciden.
 */

import { create } from 'zustand';

export type NotificationKind = 'enrollment' | 'chat' | 'update' | 'update-failed';

export interface AppNotification {
  /** ID estable mientras la notificación esté vigente. Si el id cambia, es otra notificación. */
  id: string;
  kind: NotificationKind;
  title: string;
  body: string;
  /** ISO 8601 */
  createdAt: string;
  read: boolean;
  /** Ruta a la que navega el click en "Ir". */
  actionPath?: string;
  meta?: Record<string, unknown>;
}

interface NotificationsState {
  items: AppNotification[];
  /** Mantenido como campo (no selector) para evitar re-renders innecesarios. */
  unreadTotal: number;

  // --- Mutations ---
  /**
   * Reemplaza los items de un `kind` específico, preservando `read=true` cuando
   * el `id` coincide con uno que ya estaba en el store. Esto evita que las
   * notificaciones reaparezcan como "no leídas" mientras el panel está abierto.
   */
  bulkSet: (kind: NotificationKind, items: AppNotification[]) => void;
  markRead: (id: string) => void;
  markAllRead: () => void;
  markAllReadByKind: (kind: NotificationKind) => void;
  dismiss: (id: string) => void;
  clearAll: () => void;

  // --- Persistence ---
  /** Carga el set de ids leídos desde localStorage. Idempotente. */
  hydrateReadFromStorage: () => void;
}

const STORAGE_KEY = 'ra.notifications.read';

/**
 * Lee el set de ids leídos desde localStorage. Devuelve un Set vacío si la
 * entrada no existe o está corrupta. Defensivo: SSR/tests pueden no tener
 * localStorage.
 */
function loadReadSet(): Set<string> {
  try {
    if (typeof localStorage === 'undefined') return new Set();
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed))
      return new Set(parsed.filter((x): x is string => typeof x === 'string'));
    return new Set();
  } catch {
    return new Set();
  }
}

function saveReadSet(ids: Set<string>): void {
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(ids)));
  } catch {
    // localStorage puede tirar QuotaExceededError; ignorar.
  }
}

/** Recalcula unreadTotal a partir de items. Pura. */
function recomputeUnread(items: AppNotification[]): number {
  return items.reduce((s, n) => s + (n.read ? 0 : 1), 0);
}

/**
 * Para cada item entrante, decide el flag `read`:
 * - Si el id existía antes (en oldItems), preservar su `read` previo.
 * - Si el id es nuevo, mirar el set persistido: si está ahí, marcar como leído.
 * - Si no, dejar `read` como vino del caller (normalmente false).
 */
function reconcileRead(
  incoming: AppNotification[],
  oldItems: AppNotification[],
  persistedRead: Set<string>
): AppNotification[] {
  const oldById = new Map(oldItems.map((n) => [n.id, n] as const));
  return incoming.map((n) => {
    const prev = oldById.get(n.id);
    if (prev) {
      // Mantener el flag que el store ya tenía — evita que reaparezca como no leída.
      return { ...n, read: prev.read };
    }
    if (persistedRead.has(n.id)) {
      return { ...n, read: true };
    }
    return n;
  });
}

export const useNotificationsStore = create<NotificationsState>((set, get) => {
  return {
    items: [],
    unreadTotal: 0,

    bulkSet: (kind, incoming) => {
      const state = get();
      const otherKinds = state.items.filter((n) => n.kind !== kind);
      const sameKind = state.items.filter((n) => n.kind === kind);
      const persistedRead = loadReadSet();
      const reconciled = reconcileRead(incoming, sameKind, persistedRead);
      const next = [...otherKinds, ...reconciled];
      set({ items: next, unreadTotal: recomputeUnread(next) });
    },

    markRead: (id) => {
      const state = get();
      const target = state.items.find((n) => n.id === id);
      if (!target || target.read) return; // no-op idempotente
      const next = state.items.map((n) => (n.id === id ? { ...n, read: true } : n));
      // Persistir inmediatamente el id.
      const persisted = loadReadSet();
      persisted.add(id);
      saveReadSet(persisted);
      set({ items: next, unreadTotal: recomputeUnread(next) });
    },

    markAllRead: () => {
      const state = get();
      if (state.unreadTotal === 0) return;
      const next = state.items.map((n) => ({ ...n, read: true }));
      const persisted = loadReadSet();
      next.forEach((n) => persisted.add(n.id));
      saveReadSet(persisted);
      set({ items: next, unreadTotal: 0 });
    },

    markAllReadByKind: (kind) => {
      const state = get();
      let touched = false;
      const next = state.items.map((n) => {
        if (n.kind === kind && !n.read) {
          touched = true;
          return { ...n, read: true };
        }
        return n;
      });
      if (!touched) return;
      const persisted = loadReadSet();
      next.forEach((n) => {
        if (n.kind === kind) persisted.add(n.id);
      });
      saveReadSet(persisted);
      set({ items: next, unreadTotal: recomputeUnread(next) });
    },

    dismiss: (id) => {
      const state = get();
      const next = state.items.filter((n) => n.id !== id);
      if (next.length === state.items.length) return;
      set({ items: next, unreadTotal: recomputeUnread(next) });
    },

    clearAll: () => set({ items: [], unreadTotal: 0 }),

    hydrateReadFromStorage: () => {
      const state = get();
      const persisted = loadReadSet();
      let changed = false;
      const next = state.items.map((n) => {
        if (!n.read && persisted.has(n.id)) {
          changed = true;
          return { ...n, read: true };
        }
        return n;
      });
      if (changed) set({ items: next, unreadTotal: recomputeUnread(next) });
    },
  };
});

/** Tests helpers: resetear el store entre tests. No usar en producción. */
export function __resetNotificationsStore(): void {
  useNotificationsStore.setState({ items: [], unreadTotal: 0 });
}
